import { can } from '../utils/permissions';
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import Sidebar from './Sidebar';
import './NotificationsScreen.css';
import notificationService from '../services/notificationService';
import TemplateLibrary from './TemplateLibrary';

// ── DATA ─────────────────────────────────────────────────────────────────────
const NOTIFICATIONS = [
  // REGISTRATION
  { id:'01', module:'registration', moduleLabel:'✎ Registration Module', notification:'OTP for guest login / registration', recipients:['Student'], channel:'SMS only', channelType:'sms', mode:'automated', template:'REG_OTP_01', templateStatus:'dlt', active:true },
  { id:'02', module:'registration', notification:'Registration received confirmation', recipients:['Student'], channel:'WhatsApp → SMS', channelType:'whatsapp', mode:'automated', template:'REG_CONF_02', templateStatus:'approved', active:true },
  { id:'03', module:'registration', notification:'New registration in pool alert', recipients:['Admin'], channel:'In-App + Email', channelType:'inapp', mode:'automated', template:'Internal', templateStatus:'internal', active:true },
  { id:'04', module:'registration', notification:'Admission confirmed + Student ID generated', recipients:['Student','Parent'], channel:'WhatsApp → SMS', channelType:'whatsapp', mode:'automated', template:'REG_ADM_04', templateStatus:'approved', active:true },
  { id:'05', module:'registration', notification:'Admission rejected with reason', recipients:['Student','Parent'], channel:'WhatsApp → SMS', channelType:'whatsapp', mode:'manual', template:'REG_REJ_05', templateStatus:'pending', active:true },
  // ROOM
  { id:'06', module:'room', moduleLabel:'⊞ Room Allocation Module', notification:'Room / bed allocated', recipients:['Student','Parent'], channel:'WhatsApp → SMS', channelType:'whatsapp', mode:'automated', template:'ROOM_ALLOC_06', templateStatus:'approved', active:true },
  { id:'07', module:'room', notification:'Room change intimation', recipients:['Student','Parent'], channel:'WhatsApp → SMS', channelType:'whatsapp', mode:'manual', template:'ROOM_CHG_07', templateStatus:'pending', active:true },
  // ATTENDANCE
  { id:'08', module:'attendance', moduleLabel:'⊙ Attendance & Biometrics Module', notification:'Post 10 PM — consolidated IN/OUT list (PDF)', recipients:['Admin','Owner'], channel:'WA + Email', channelType:'both', mode:'scheduled', template:'ATT_CONS_08', templateStatus:'approved', active:true },
  { id:'09', module:'attendance', notification:'Post 10 PM — individual student status to parent', recipients:['Parent'], channel:'WhatsApp → SMS', channelType:'whatsapp', mode:'scheduled', template:'ATT_IND_09', templateStatus:'approved', active:true },
  { id:'10', module:'attendance', notification:'Leave application submitted (with Approve / Reject buttons)', recipients:['Parent'], channel:'WhatsApp → SMS link', channelType:'whatsapp', mode:'automated', template:'LEAVE_REQ_10', templateStatus:'pending', active:true },
  { id:'11', module:'attendance', notification:'Leave approved / rejected by parent', recipients:['Student','Admin'], channel:'WhatsApp → SMS', channelType:'whatsapp', mode:'automated', template:'LEAVE_PAR_11', templateStatus:'approved', active:true },
  { id:'12', module:'attendance', notification:'Student not returned after leave expiry', recipients:['Admin','Parent'], channel:'WA + SMS both', channelType:'both', mode:'automated', template:'LEAVE_EXP_12', templateStatus:'approved', active:true },
  // INVENTORY
  { id:'13', module:'inventory', moduleLabel:'▣ Inventory Module', notification:'Low stock alert', recipients:['Admin','Owner'], channel:'WhatsApp → SMS', channelType:'whatsapp', mode:'automated', template:'INV_LOW_13', templateStatus:'pending', active:true },
  { id:'14', module:'inventory', notification:'Purchase requisition raised', recipients:['Admin','Owner'], channel:'In-App + Email', channelType:'inapp', mode:'automated', template:'Internal', templateStatus:'internal', active:true },
  { id:'15', module:'inventory', notification:'Stock received confirmation', recipients:['Admin','Owner'], channel:'In-App + Email', channelType:'inapp', mode:'automated', template:'Internal', templateStatus:'internal', active:true },
  // FINANCE
  { id:'16', module:'finance', moduleLabel:'₹ Finance Module', notification:'Fee due reminder — 7 days prior', recipients:['Student','Parent'], channel:'WhatsApp → SMS', channelType:'whatsapp', mode:'automated', template:'FIN_DUE7_16', templateStatus:'approved', active:true },
  { id:'17', module:'finance', notification:'Fee due reminder — 1 day prior', recipients:['Student','Parent'], channel:'WhatsApp → SMS', channelType:'whatsapp', mode:'automated', template:'FIN_DUE1_17', templateStatus:'approved', active:true },
  { id:'18', module:'finance', notification:'Fee overdue alert', recipients:['Student','Parent'], channel:'WA + SMS both', channelType:'both', mode:'automated', template:'FIN_OVR_18', templateStatus:'approved', active:true, critical:true },
  { id:'19', module:'finance', notification:'Payment received + receipt (PDF)', recipients:['Student','Parent'], channel:'WhatsApp PDF → Email', channelType:'whatsapp', mode:'automated', template:'FIN_RCPT_19', templateStatus:'approved', active:true },
  { id:'20', module:'finance', notification:'Daily fee collection statement (PDF)', recipients:['Admin','Owner'], channel:'WA PDF + Email', channelType:'both', mode:'scheduled', template:'FIN_DAILY_20', templateStatus:'approved', active:true },
  { id:'21', module:'finance', notification:'Pay card low balance alert', recipients:['Student','Parent'], channel:'WhatsApp → SMS', channelType:'whatsapp', mode:'automated', template:'PAY_LOW_21', templateStatus:'approved', active:true },
  { id:'22', module:'finance', notification:'Pay card weekly transaction statement (PDF)', recipients:['Parent'], channel:'WhatsApp PDF → Email', channelType:'whatsapp', mode:'scheduled', template:'PAY_STMT_22', templateStatus:'approved', active:true },
  // CANTEEN
  { id:'23', module:'canteen', moduleLabel:'🍽 Canteen Module', notification:'Monthly menu for upcoming month (PDF broadcast)', recipients:['Student','Parent'], channel:'WhatsApp PDF + Email', channelType:'whatsapp', mode:'manual', template:'CAN_MENU_23', templateStatus:'pending', active:true },
  { id:'24', module:'canteen', notification:'Canteen timing change', recipients:['Student'], channel:'WhatsApp → SMS', channelType:'whatsapp', mode:'manual', template:'CAN_TIME_24', templateStatus:'pending', active:true },
  // ANNOUNCEMENTS
  { id:'25', module:'announcements', moduleLabel:'🔔 Announcements & Emergencies', notification:'Festival greetings (branded image template)', recipients:['Student','Parent'], channel:'WhatsApp image', channelType:'whatsapp', mode:'manual', template:'ANN_FEST_25', templateStatus:'none', active:true },
  { id:'26', module:'announcements', notification:'Hostel event / activity announcement', recipients:['Student','Parent'], channel:'WhatsApp → SMS', channelType:'whatsapp', mode:'manual', template:'ANN_EVT_26', templateStatus:'none', active:true },
  { id:'27', module:'announcements', notification:'Maintenance / utility downtime', recipients:['Student','Parent'], channel:'WhatsApp → SMS', channelType:'whatsapp', mode:'manual', template:'ANN_MAINT_27', templateStatus:'none', active:true },
  { id:'28', module:'announcements', notification:'Emergency broadcast (evacuation / fire drill)', recipients:['Student','Parent'], channel:'WA + SMS both', channelType:'both', mode:'manual', template:'ANN_EMER_28', templateStatus:'approved', active:true, critical:true },
];

const CHANNEL_BADGE = {
  whatsapp: { cls:'badge-whatsapp' },
  sms:      { cls:'badge-sms' },
  both:     { cls:'badge-both' },
  email:    { cls:'badge-email' },
  inapp:    { cls:'badge-inapp' },
};

const MODE_BADGE = {
  automated: { cls:'badge-auto',      label:'Automated' },
  manual:    { cls:'badge-manual',    label:'Manual' },
  scheduled: { cls:'badge-scheduled', label:'Scheduled' },
};

const RECIPIENT_CLS = { Owner:'rpill owner', Parent:'rpill parent', Student:'rpill student', Admin:'rpill admin' };

const MODULES = [
  { value:'', label:'All Modules' },
  { value:'registration', label:'Registration' },
  { value:'room', label:'Room Allocation' },
  { value:'attendance', label:'Attendance' },
  { value:'inventory', label:'Inventory' },
  { value:'finance', label:'Finance' },
  { value:'canteen', label:'Canteen' },
  { value:'announcements', label:'Announcements' },
];

// ── TOGGLE ────────────────────────────────────────────────────────────────────
const Toggle = ({ checked, onChange }) => (
  <label className="n-toggle">
    <input type="checkbox" checked={checked} onChange={onChange}/>
    <span className="n-slider"/>
  </label>
);

// ── MAIN COMPONENT ─────────────────────────────────────────────────────────────
export default function NotificationsScreen({ onNavigate, onLogout }) {
  const [apiStats,    setApiStats]    = useState({ total:28, approved:16, pending:6, automated:18, critical:3 });
  const [matrixData,  setMatrixData]  = useState([]);
  const [matrixLoad,  setMatrixLoad]  = useState(true);
  const [tab,         setTab]         = useState('matrix');
  const [search,      setSearch]      = useState('');
  const [modFilter,   setModFilter]   = useState('');
  const [chanFilter,  setChanFilter]  = useState('');
  const [modeFilter,  setModeFilter]  = useState('');
  const [activeMap,   setActiveMap]   = useState({});

  // sync activeMap when templates load
  useEffect(() => {
    if (matrixData.length > 0) {
      const m = {};
      matrixData.forEach(t => { m[t.templateId] = t.active !== false; });
      setActiveMap(m);
    }
  }, [matrixData]);

  const pendingCount = apiStats?.pending || 0;

  useEffect(() => {
    notificationService.getStats().then(setApiStats).catch(() => {});
    notificationService.getTemplates().then(data => {
      setMatrixData(data);
      setMatrixLoad(false);
    }).catch(() => setMatrixLoad(false));
  }, []);

  const toggleActive = useCallback((id) => {
    const newVal = !activeMap[id];
    setActiveMap(prev => ({ ...prev, [id]: newVal }));
    notificationService.toggleActive(id, newVal).catch(() => {
      // revert on failure
      setActiveMap(prev => ({ ...prev, [id]: !newVal }));
    });
  }, [activeMap]);

  // ── filter ──────────────────────────────────────────────────────────────────
  // Convert API template to matrix row format
  const toMatrixRow = (t) => ({
    id: t.templateId,
    module: (t.module||'').toLowerCase(),
    notification: t.name,
    recipients: t.recipients || [],
    channel: { wa:'WhatsApp → SMS', sms:'SMS only', both:'WA + SMS both' }[t.channel] || t.channel,
    channelType: t.channel,
    mode: t.mode,
    template: t.templateId,
    templateStatus: t.approvalStatus,
    active: t.active !== false,
    critical: t.critical,
  });

  const MODULE_LABELS = {
    registration: '✎ Registration Module',
    room: '⊞ Room Allocation Module',
    attendance: '⊙ Attendance & Biometrics Module',
    inventory: '▣ Inventory Module',
    finance: '₹ Finance Module',
    canteen: '🍽 Canteen Module',
    announcements: '🔔 Announcements & Emergencies',
  };

  const filtered = useMemo(() => {
    const rows = matrixData.map(toMatrixRow);
    return rows.filter(n => {
      const matchSearch = !search || n.notification.toLowerCase().includes(search.toLowerCase()) || n.template.toLowerCase().includes(search.toLowerCase());
      const matchMod    = !modFilter  || n.module === modFilter;
      const matchChan   = !chanFilter || n.channelType === chanFilter;
      const matchMode   = !modeFilter || n.mode === modeFilter;
      return matchSearch && matchMod && matchChan && matchMode;
    });
  }, [matrixData, search, modFilter, chanFilter, modeFilter]);

  const grouped = useMemo(() => {
    const groups = {};
    const order  = [];
    filtered.forEach(n => {
      if (!groups[n.module]) { groups[n.module] = []; order.push(n.module); }
      groups[n.module].push(n);
    });
    return order.map(mod => ({ mod, items: groups[mod] }));
  }, [filtered]);

  // ── stats ───────────────────────────────────────────────────────────────────
  const stats = [
    { num: apiStats.total,     label:'Total Templates',    cls:'c1' },
    { num: apiStats.automated, label:'Automated',          cls:'c2' },
    { num: (apiStats.total - apiStats.automated), label:'Manual / Scheduled', cls:'c3' },
    { num: apiStats.approved,  label:'Meta Approved',      cls:'c4' },
    { num: apiStats.critical,  label:'Critical',           cls:'c5' },
  ];

  return (
    <div className="app-layout">
      <Sidebar activeItem="notifications" onNavigate={onNavigate} onLogout={onLogout}/>
      <main className="main-content" style={{ padding:0, display:'flex', flexDirection:'column', overflow:'hidden' }}>

        {/* TOPBAR */}
        <div className="n-topbar">
          <div className="n-breadcrumb">System / <span>Notifications</span></div>
          <div style={{ display:'flex', gap:10, alignItems:'center' }}>
            <span style={{ fontSize:11.5, color:'#888' }}>Template approvals pending:</span>
            <span className="n-pending-badge">{pendingCount} pending</span>
          </div>
        </div>

        {/* PAGE HEADER */}
        <div className="n-page-header">
          <div className="n-page-title">Notifications</div>
          <button className="n-btn-primary">+ SEND MANUAL NOTIFICATION</button>
        </div>

        {/* STATS */}
        <div className="n-stats-row">
          {stats.map(s => (
            <div key={s.label} className={`n-stat-card ${s.cls}`}>
              <div className="n-stat-num">{s.num}</div>
              <div className="n-stat-label">{s.label}</div>
            </div>
          ))}
        </div>

        {/* TABS */}
        <div className="n-tabs-wrap">
          <div className="n-tabs">
            {[
              { id:'matrix',   label:'Notification Matrix' },
              { id:'log',      label:'Sent Log' },
              can('NOTIFICATION_TEMPLATES') ? { id:'library',  label:'Template Library' } : null,
              { id:'settings', label:'Channel Settings' },
            ].filter(Boolean).map(t => (
              <div key={t.id} className={`n-tab${tab===t.id?' active':''}`} onClick={() => setTab(t.id)}>{t.label}</div>
            ))}
          </div>
        </div>

        {/* CONTENT */}
        <div className="n-content">

          {tab === 'matrix' && (
            <>
              {matrixLoad && <div style={{textAlign:'center',padding:40,color:'#888'}}>Loading…</div>}
              {/* Info banner */}
              <div className="n-info-banner">
                <strong>Routing rule:</strong> WhatsApp Business API is primary. SMS Gateway is the fallback when recipient has not opted into WhatsApp.
                Fee overdue and Emergency alerts are sent on <strong>both channels simultaneously</strong> regardless of opt-in status.
              </div>

              {/* Filter bar */}
              <div className="n-filter-bar">
                <div className="n-search-wrap">
                  <span className="n-search-icon">🔍</span>
                  <input
                    type="text" placeholder="Search notifications by name or module..."
                    value={search} onChange={e => setSearch(e.target.value)}
                  />
                </div>
                <select className="n-filter-select" value={modFilter} onChange={e => setModFilter(e.target.value)}>
                  {MODULES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
                <select className="n-filter-select" value={chanFilter} onChange={e => setChanFilter(e.target.value)}>
                  <option value="">All Channels</option>
                  <option value="whatsapp">WhatsApp</option>
                  <option value="sms">SMS</option>
                  <option value="both">Both</option>
                  <option value="inapp">In-App</option>
                </select>
                <select className="n-filter-select" value={modeFilter} onChange={e => setModeFilter(e.target.value)}>
                  <option value="">All Modes</option>
                  <option value="automated">Automated</option>
                  <option value="manual">Manual</option>
                  <option value="scheduled">Scheduled</option>
                </select>
              </div>

              {/* Table */}
              <table className="n-table">
                <thead>
                  <tr>
                    <th style={{ width:32 }}>#</th>
                    <th>Notification</th>
                    <th>Recipients</th>
                    <th>Channel</th>
                    <th>Mode</th>
                    <th>Template</th>
                    <th style={{ width:60, textAlign:'center' }}>Active</th>
                  </tr>
                </thead>
                <tbody>
                  {grouped.map(({ mod, items }) => (
                    <React.Fragment key={mod}>
                      <tr className="n-module-group">
                        <td colSpan={7}>{MODULE_LABELS[mod] || mod}</td>
                      </tr>
                      {items.map(n => (
                        <tr key={n.id}>
                          <td className="n-muted">{n.id}</td>
                          <td>
                            {n.notification}
                            {n.critical && <span className="n-critical">⚠ CRITICAL</span>}
                          </td>
                          <td>
                            <div className="n-recipients">
                              {n.recipients.map(r => (
                                <span key={r} className={RECIPIENT_CLS[r] || 'rpill'}>{r}</span>
                              ))}
                            </div>
                          </td>
                          <td>
                            <span className={`n-badge ${CHANNEL_BADGE[n.channelType]?.cls || ''}`}>{n.channel}</span>
                          </td>
                          <td>
                            <span className={`n-badge ${MODE_BADGE[n.mode]?.cls || ''}`}>{MODE_BADGE[n.mode]?.label || n.mode}</span>
                          </td>
                          <td>
                            <span className="n-template-code">{n.template}</span>
                            {n.templateStatus === 'approved' && <span className="n-tmpl-badge approved">Approved ✓</span>}
                            {n.templateStatus === 'dlt'      && <span className="n-tmpl-badge dlt">DLT ✓</span>}
                            {n.templateStatus === 'pending'  && <span className="n-tmpl-badge pending">Pending</span>}
                          </td>
                          <td style={{ textAlign:'center' }}>
                            <Toggle checked={!!activeMap[n.id]} onChange={() => toggleActive(n.id)}/>
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                  {grouped.length === 0 && (
                    <tr><td colSpan={7} style={{ textAlign:'center', padding:40, color:'#999' }}>No notifications match your filters.</td></tr>
                  )}
                </tbody>
              </table>
            </>
          )}

          {tab === 'log' && <SentLog />}

          {tab === 'settings' && (
            <div style={{ textAlign:'center', padding:60, color:'#999', fontSize:14 }}>
              Channel settings — coming soon
            </div>
          )}

          {tab === 'library' && can('NOTIFICATION_TEMPLATES') && (
            <div style={{ flex:1, overflow:'hidden', display:'flex', minHeight:0, margin:'-20px -28px', height:'calc(100% + 40px)' }}>
              <TemplateLibrary/>
            </div>
          )}

        </div>
      </main>
    </div>
  );
}
// ── SENT LOG COMPONENT ───────────────────────────────────────────────────────
function SentLog() {
  const [logs,      setLogs]      = useState({ content:[], totalElements:0, totalPages:0 });
  const [logStats,  setLogStats]  = useState({});
  const [loading,   setLoading]   = useState(true);
  const [page,      setPage]      = useState(0);
  const [filters,   setFilters]   = useState({ templateId:'', module:'', status:'', search:'' });

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const [logsData, statsData] = await Promise.all([
        notificationService.getLogs({ ...filters, page, size:20 }),
        notificationService.getLogStats(),
      ]);
      setLogs(logsData);
      setLogStats(statsData);
    } catch(e) {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const STATUS_CLS  = { SUCCESS:'log-success', FAILED:'log-failed' };
  const CHAN_LABELS  = { whatsapp:'💬 WhatsApp', sms:'📱 SMS', both:'💬+📱 Both' };

  return (
    <div className="n-log-wrap">
      {/* Log stats */}
      <div className="n-log-stats">
        {[
          { label:'Total Sent',  val: logStats.total   || 0, cls:'ls1' },
          { label:'Success',     val: logStats.success || 0, cls:'ls2' },
          { label:'Failed',      val: logStats.failed  || 0, cls:'ls3' },
          { label:'Today',       val: logStats.today   || 0, cls:'ls4' },
        ].map(s => (
          <div key={s.label} className={`n-log-stat ${s.cls}`}>
            <div className="n-log-stat-num">{s.val}</div>
            <div className="n-log-stat-label">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="n-filter-bar" style={{ marginBottom:12 }}>
        <div className="n-search-wrap">
          <span className="n-search-icon">🔍</span>
          <input
            type="text" placeholder="Search by name or mobile..."
            value={filters.search}
            onChange={e => { setFilters(f => ({...f, search:e.target.value})); setPage(0); }}
          />
        </div>
        <select className="n-filter-select"
          value={filters.module}
          onChange={e => { setFilters(f => ({...f, module:e.target.value})); setPage(0); }}>
          <option value="">All Modules</option>
          <option value="registration">Registration</option>
          <option value="attendance">Attendance</option>
          <option value="finance">Finance</option>
          <option value="inventory">Inventory</option>
          <option value="canteen">Canteen</option>
          <option value="announcements">Announcements</option>
        </select>
        <select className="n-filter-select"
          value={filters.status}
          onChange={e => { setFilters(f => ({...f, status:e.target.value})); setPage(0); }}>
          <option value="">All Status</option>
          <option value="SUCCESS">Success</option>
          <option value="FAILED">Failed</option>
        </select>
        <button className="n-btn-outline" onClick={fetchLogs}>↻ Refresh</button>
      </div>

      {/* Table */}
      {loading ? (
        <div style={{ textAlign:'center', padding:40, color:'#888' }}>Loading logs…</div>
      ) : (
        <>
          <table className="n-table">
            <thead>
              <tr>
                <th>Time</th>
                <th>Template</th>
                <th>Recipient</th>
                <th>Mobile</th>
                <th>Channel</th>
                <th>Status</th>
                <th>Response</th>
              </tr>
            </thead>
            <tbody>
              {logs.content && logs.content.map(log => (
                <tr key={log.id}>
                  <td className="n-muted" style={{ whiteSpace:'nowrap', fontSize:11.5 }}>
                    {log.sentAt ? new Date(log.sentAt).toLocaleString('en-IN', {
                      day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit'
                    }) : '—'}
                  </td>
                  <td>
                    <span className="n-template-code">{log.templateId}</span>
                    {log.module && <span className="n-muted" style={{fontSize:11, marginLeft:4}}>({log.module})</span>}
                  </td>
                  <td>{log.recipientName || '—'}</td>
                  <td className="n-muted" style={{ fontSize:11.5 }}>{log.mobile || '—'}</td>
                  <td>
                    <span className={`n-badge ${log.channel === 'whatsapp' ? 'badge-whatsapp' : log.channel === 'sms' ? 'badge-sms' : 'badge-both'}`}>
                      {CHAN_LABELS[log.channel] || log.channel || '—'}
                    </span>
                  </td>
                  <td>
                    <span className={`n-tmpl-badge ${STATUS_CLS[log.status] || ''}`}>
                      {log.status === 'SUCCESS' ? '✓ Sent' : '✗ Failed'}
                    </span>
                  </td>
                  <td className="n-muted" style={{ fontSize:11, maxWidth:160, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}
                    title={log.errorMessage || log.response || ''}>
                    {log.errorMessage || log.response || '—'}
                  </td>
                </tr>
              ))}
              {(!logs.content || logs.content.length === 0) && (
                <tr><td colSpan={7} style={{ textAlign:'center', padding:40, color:'#999' }}>No log entries found.</td></tr>
              )}
            </tbody>
          </table>

          {/* Pagination */}
          {logs.totalPages > 1 && (
            <div className="n-pagination">
              <button disabled={page === 0} onClick={() => setPage(p => p-1)} className="n-btn-outline">← Prev</button>
              <span style={{ fontSize:12, color:'#666' }}>
                Page {page+1} of {logs.totalPages} &nbsp;·&nbsp; {logs.totalElements} entries
              </span>
              <button disabled={page >= logs.totalPages-1} onClick={() => setPage(p => p+1)} className="n-btn-outline">Next →</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}